import React, { useState } from 'react';
import { Box, TextField, FormControlLabel, Checkbox, Typography, Link, FormControl, Select, MenuItem, InputLabel } from '@mui/material';
import { motion, useAnimation } from 'framer-motion';
import { useInView } from 'react-intersection-observer';

const viewportConfig = { 
  threshold: 0.1,
  triggerOnce: false,
  rootMargin: "-50px 0px"
};

const containerVariants = {
  hidden: { opacity: 0, y: 50 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.5,
      ease: "easeOut",
      staggerChildren: 0.1,
      when: "beforeChildren"
    }
  }
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.4,
      ease: "easeOut"
    }
  }
};

const inquiryTypes = [
  'Mortgage Application',
  'Refinancing',
  'Home Purchase',
  'Investment Property',
  'General Inquiry'
];

const ContactForm = () => {
  const [ref, inView] = useInView(viewportConfig);
  const controls = useAnimation();

  const [formData, setFormData] = useState({
    name: '',
    inquiryType: '',
    email: '',
    phone: '',
    address: '',
    description: '',
    termsAccepted: false
  });

  React.useEffect(() => {
    controls.start(inView ? "visible" : "hidden");
  }, [controls, inView]);

  const handleChange = (event) => {
    const { name, value, checked } = event.target;
    setFormData(prev => ({
      ...prev,
      [name]: name === 'termsAccepted' ? checked : value
    }));
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    console.log('Form submitted:', formData);
  };

  const inputStyles = {
    '& .MuiOutlinedInput-root': {
      backgroundColor: '#fff',
      '& fieldset': {
        borderColor: '#E0E0E0'
      }
    },
    '& .MuiInputLabel-root': {
      color: '#1A1A1A'
    },
    '& .MuiInputLabel-asterisk': {
      color: '#FF0000'
    }
  };

  return (
    <Box
      ref={ref}
      component={motion.form}
      variants={containerVariants}
      initial="hidden"
      animate={controls}
      onSubmit={handleSubmit}
      sx={{
        width: '100%',
        aspectRatio: '5/4',
        backgroundColor: '#F7F9FC',
        p: { xs: 4, md: 6 },
        pb: { xs: 4, md: 20 },
        borderRadius: '8px',
        display: 'flex',
        flexDirection: 'column',
        boxShadow: '0 4px 10px rgba(0, 0, 0, 0.15)'
      }}
    >
      <Box sx={{ 
        display: 'grid', 
        gap: 4,
        flex: 1,
        gridTemplateRows: 'auto auto auto 1fr auto auto'
      }}>
        <Box 
          component={motion.div}
          variants={itemVariants}
          sx={{ 
            display: 'grid', 
            gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, 
            gap: 4 
          }}
        >
          <TextField
            required
            fullWidth
            label="Name"
            name="name"
            value={formData.name}
            onChange={handleChange}
            placeholder="Enter your full name"
            sx={inputStyles}
          />

          <FormControl required fullWidth sx={inputStyles}>
            <InputLabel>Inquiry Type</InputLabel>
            <Select
              name="inquiryType"
              value={formData.inquiryType}
              onChange={handleChange}
              label="Inquiry Type"
              placeholder="Select Inquiry type"
              MenuProps={{ disableScrollLock: true }}
            >
              {inquiryTypes.map((type) => (
                <MenuItem key={type} value={type}>
                  {type}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        <Box 
          component={motion.div}
          variants={itemVariants}
          sx={{ 
            display: 'grid', 
            gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, 
            gap: 4 
          }}
        >
          <TextField
            required
            fullWidth
            label="Email Address"
            name="email"
            type="email"
            value={formData.email}
            onChange={handleChange}
            placeholder="Enter your email address"
            sx={inputStyles}
          />

          <TextField
            required
            fullWidth
            label="Phone Number"
            name="phone"
            value={formData.phone}
            onChange={handleChange}
            placeholder="Enter your phone number"
            sx={inputStyles}
          />
        </Box>

        <Box component={motion.div} variants={itemVariants}>
          <TextField
            fullWidth
            label="Contact Address"
            name="address"
            value={formData.address}
            onChange={handleChange}
            placeholder="Enter your contact address"
            multiline
            rows={2}
            sx={inputStyles}
          />
        </Box>

        <Box component={motion.div} variants={itemVariants}>
          <TextField
            required
            fullWidth
            label="Brief Description"
            name="description"
            value={formData.description}
            onChange={handleChange}
            placeholder="Enter your brief description here"
            multiline
            rows={4}
            sx={{
              ...inputStyles,
              height: '100%',
              '& .MuiInputBase-root': {
                height: '100%'
              }
            }}
          />
        </Box>

        <Box component={motion.div} variants={itemVariants}>
          <FormControlLabel
            control={
              <Checkbox
                name="termsAccepted"
                checked={formData.termsAccepted}
                onChange={handleChange}
                sx={{
                  color: '#0066CC',
                  '&.Mui-checked': {
                    color: '#0066CC'
                  }
                }}
              />
            }
            label={
              <Typography variant="body2" sx={{ color: '#1A1A1A' }}>
                Check here to indicate that you have read and agree to the our{' '}
                <Link 
                  href="#" 
                  aria-label="Read our terms and conditions"
                  sx={{ color: '#0066CC', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
                >
                  terms and conditions
                </Link>
                .
              </Typography>
            }
          />
        </Box>

        <Box 
          component={motion.div} 
          variants={itemVariants}
          sx={{ display: 'flex', justifyContent: 'flex-end' }}
        >
          <button
            type="submit"
            disabled={!formData.termsAccepted}
            style={{
              backgroundColor: '#0066CC',
              color: '#fff',
              border: 'none',
              borderRadius: '4px',
              padding: '12px 24px',
              cursor: 'pointer',
              fontSize: '1rem',
              fontWeight: 500,
              opacity: !formData.termsAccepted ? 0.7 : 1
            }}
          >
            Submit
          </button>
        </Box>
      </Box>
    </Box>
  );
};

export default ContactForm; 